import React, { Suspense, useEffect, useRef, useState } from 'react'
import lazyWithRetry from '../../utils/lazyWithRetry'
import { useTranslation } from 'react-i18next'
import InfiniteScroll from 'react-infinite-scroll-component'
import { studentAlerts } from '../../constants/StudentAnalyticsConstants'
import AlertsModal from '../modals/AlertsModal'
import AlertsNavigationButtons from './alerts/AlertsNavigationButtons'
import AssignFilter from './alerts/AssignFilter'
import { findAlerts } from '../../server/server'
import { usePageTitle, useQuery } from '../../utils/util'
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min'
import { useUser } from '../../auth'
import { alertsFilterList, assignmentFilterList } from './constants/AlertsConstants'
import { oneOfCodec, removeUrlParams, useUrlState } from '../../hooks/useUrlState'
import { useYearClassFilter } from '../../hooks/useYearClassFilter'
import SkeletonAlertsHeader from './skeleton/SkeletonAlertsHeader'
import SkeletonAllAlerts from './skeleton/SkeletonAllAlerts'

const AlertsHeaderSection = lazyWithRetry(() => import('./AlertsHeaderSection'), './AlertsHeaderSection')
const AllAlerts = lazyWithRetry(() => import('./alerts/AllAlerts'), './alerts/AllAlerts')

const EMPTY_FILTER_OPTIONS = { filterYears: [], classesOfYear: {} }

// The server answers with {all: [class names], '<year>': [class names]}, built
// from the classes this teacher's role covers.
const toFilterOptions = (filter) => ({
    filterYears: Object.keys(filter)
        .filter((year) => year !== 'all')
        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true })),
    classesOfYear: filter,
})

function AlertsHome() {
    const { t, locale } = useTranslation()
    const query = useQuery()
    const { user } = useUser()
    const alertId = query.get('id')
    const [selContent, setSelContent] = useUrlState('tab', 'all', oneOfCodec(alertsFilterList))
    const [loading, setLoading] = useState(true)
    const [data, setData] = useState(studentAlerts)
    const [modalIsOpen, setModalIsOpen] = useState(false)
    const [resultNum, setResultNum] = useState(0)
    const [hasMore, setHasMore] = useState(false)
    const [loadingMore, setLoadingMore] = useState(false)
    const [selectedFilter, setSelectedFilter] = useUrlState('filter', assignmentFilterList[0], oneOfCodec(assignmentFilterList))
    // Unlike Inactivity and Class Management, alerts are paged from the server,
    // so the year/class filter is applied there too (a client-side filter would
    // only see the pages loaded so far). The options come back with the first page.
    const [filterOptions, setFilterOptions] = useState(EMPTY_FILTER_OPTIONS)
    const yearClassFilter = useYearClassFilter('years', 'classes', filterOptions)
    const { appliedYearFilter, appliedClassFilter } = yearClassFilter
    const currentRequestIdRef = useRef(0)
    const history = useHistory()

    useEffect(() => {
        if (!user.alert_terms)
            setTimeout(() => {
                openAlertModal()
            }, 200)
    }, [])

    useEffect(() => {
        loadData({ loadedData: resultNum, typeFilter: selContent, assignedFilter: selectedFilter })
    }, [resultNum])

    const reloadData = () => {
        if (resultNum === 0) loadData({ loadedData: resultNum, typeFilter: selContent, assignedFilter: selectedFilter })
        else setResultNum(0)
    }

    // Keyed on the contents, not the arrays: Apply with an unchanged selection
    // hands back a fresh array and shouldn't refetch. Skips the first render,
    // where the [resultNum] effect above already does the initial load.
    const yearClassKey = JSON.stringify([appliedYearFilter, appliedClassFilter])
    const lastYearClassKeyRef = useRef(yearClassKey)
    useEffect(() => {
        if (lastYearClassKeyRef.current === yearClassKey) return
        lastYearClassKeyRef.current = yearClassKey
        reloadData()
    }, [yearClassKey])

    const loadData = async ({ loadedData = resultNum, typeFilter = selContent, assignedFilter = selectedFilter, years = appliedYearFilter, classes = appliedClassFilter }) => {
        if (resultNum === 0) {
            setLoading(true)
        } else {
            setLoadingMore(true)
        }
        const requestId = Date.now()
        currentRequestIdRef.current = requestId
        const resp = await findAlerts({ typeFilter, assignedFilter, loadedData: loadedData, years, classes })
        if (requestId !== currentRequestIdRef.current) {
            setLoadingMore(false)
            setLoading(false)
            return
        }
        if (resp.status === 200) {
            if (loadedData === 0 && resp?.data?.filter) setFilterOptions(toFilterOptions(resp.data.filter))
            const total = resp?.data?.total
            const alerts = resp?.data?.alerts
            const totalAlerts = resultNum === 0 ? alerts : [...data, ...alerts]
            setData(totalAlerts)
            if (resultNum === 0) {
                setHasMore(total > alerts?.length)
            } else {
                setHasMore(total > data?.length + alerts?.length)
            }
            if (!!alertId) {
                const found = totalAlerts.find((alert) => alert.id === alertId)
                if (found) {
                    setTimeout(() => {
                        const alertElement = document.getElementById(`alert-${alertId}`)
                        if (alertElement) {
                            alertElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
                            alertElement.classList.add('highlight-alert')
                            setTimeout(() => {
                                // Drop only the deep-link id; the tab and filter
                                // params must survive.
                                removeUrlParams(history, 'id')
                                alertElement.classList.remove('highlight-alert')
                            }, 2000)
                        }
                    }, 300)
                } else {
                    if (resultNum === 0 && total > alerts?.length) {
                        setResultNum(totalAlerts?.length)
                    } else if (resultNum !== 0 && total > data?.length + alerts?.length) {
                        setResultNum(totalAlerts?.length)
                    } else {
                        // Alert isn't in this filter's results; give up on the
                        // deep link but keep the rest of the query string.
                        removeUrlParams(history, 'id')
                    }
                }
            }
            setLoadingMore(false)
            setLoading(false)
        } else {
            setData([])
            setLoading(false)
            setLoadingMore(false)
        }
    }

    const closeAlertModal = () => {
        setModalIsOpen(false)
    }
    const openAlertModal = () => {
        setModalIsOpen(true)
    }

    const selectFilter = (filter) => {
        setSelContent(filter)
        if (resultNum === 0) loadData({ loadedData: resultNum, typeFilter: filter, assignedFilter: selectedFilter })
        else {
            setResultNum(0)
        }
    }

    const selectAssignedFilter = (filter) => {
        setSelectedFilter(filter)
        if (resultNum === 0) loadData({ loadedData: resultNum, typeFilter: selContent, assignedFilter: filter })
        else setResultNum(0)
    }

    // A single class has nothing to choose between, so the dropdown stays out of
    // the way - unless a filter from the URL is active and needs a way to reset.
    const hasActiveYearClassFilter = !appliedYearFilter.includes('all') || !appliedClassFilter.includes('all')
    const showYearClassFilter = (filterOptions.classesOfYear.all?.length ?? 0) > 1 || hasActiveYearClassFilter

    usePageTitle(`Upstrive Education - Alerts`)
    return (
        <div id="alerts-scroll" className="analytics-container light-small-scroll">
            <InfiniteScroll
                dataLength={data.length}
                next={() => {
                    if (!loading && !loadingMore) {
                        setResultNum(data?.length)
                    }
                }}
                hasMore={hasMore}
                scrollableTarget="alerts-scroll"
                // At least a screen tall (minus the container's 2x40px padding and this
                // 40px margin), so a short list still leaves room for the filter
                // dropdown, which opens over it. InfiniteScroll gives this element
                // overflow:auto, which would otherwise clip the panel.
                className="analytics-inner-container mb-[40px] min-h-[calc(100vh-120px)]"
                scrollThreshold={0.8}
            >
                <div className="w-full bg-white rounded-[10px] flex flex-col">
                    <Suspense fallback={<SkeletonAlertsHeader />}>
                        <AlertsHeaderSection
                            selContent={selContent}
                            selectFilter={selectFilter}
                            selectedFilter={selectedFilter}
                            selectAssignedFilter={selectAssignedFilter}
                            yearClassFilter={yearClassFilter}
                            filterYears={filterOptions.filterYears}
                            showYearClassFilter={showYearClassFilter}
                            loadingFilter={loading}
                        />
                    </Suspense>
                </div>
                <div className="w-full mt-5 flex-1 flex flex-col">
                    <Suspense fallback={<SkeletonAllAlerts />}>
                        <AllAlerts data={data} loading={loading} loadData={reloadData} />
                    </Suspense>
                </div>
            </InfiniteScroll>
            <AlertsModal modalIsOpen={modalIsOpen} closeModal={closeAlertModal} />
        </div>
    )
}

export default AlertsHome
